'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Comment } from '@/types/comment';
import { useComments } from '@/hooks/use-comments';
import { EMPTY_COMMENTS, useCommentsStore } from '@/store/comments.store';
import type { CommentListSort } from '../types/publication-kind';
import { getCommentsScopeKey } from '../utils/get-comments-scope-key.util';
import { useCommentReactions } from './use-comment-reactions';

export type UseCommentSectionParams = {
  articleId?: number;
  postId?: number;
  chapterId?: string;
  paragraphId?: string;
  sort?: CommentListSort;
  onCommentsChange?: () => void;
  onHasNextPageChange?: (hasNextPage: boolean) => void;
};

export function useCommentSection({
  articleId,
  postId,
  chapterId,
  paragraphId,
  sort,
  onCommentsChange,
  onHasNextPageChange,
}: UseCommentSectionParams) {
  const isParagraphMode = Boolean(postId && chapterId && paragraphId);
  const isChapterMode = Boolean(postId && chapterId && !paragraphId);
  const scopeKey = getCommentsScopeKey({ articleId, postId, chapterId, paragraphId });
  const { fetchComments, fetchReplies, reportComment, deleteComment, updateComment } =
    useComments(scopeKey);
  const comments = useCommentsStore((state) => state.scopes[scopeKey]?.comments ?? EMPTY_COMMENTS);
  const loading = useCommentsStore((state) => state.scopes[scopeKey]?.loading ?? false);
  const error = useCommentsStore((state) => state.scopes[scopeKey]?.error ?? null);
  const topLevelComments = useMemo(
    () => comments.filter((comment) => !comment.parentComment),
    [comments],
  );

  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set());
  const [repliesLoading, setRepliesLoading] = useState<Set<number>>(new Set());
  const [loadedReplies, setLoadedReplies] = useState<Map<number, Comment[]>>(new Map());
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    hasNextPage: true,
    totalDocs: 0,
  });

  const reactions = useCommentReactions();

  const fetchPage = useCallback(
    (page: number) => {
      if (isParagraphMode) {
        return fetchComments(undefined, postId, chapterId, paragraphId, page);
      }
      if (isChapterMode) {
        return fetchComments(undefined, postId, chapterId, undefined, page);
      }
      if (articleId || postId) {
        return fetchComments(articleId, postId, undefined, undefined, page, sort);
      }
      return Promise.resolve(undefined);
    },
    [
      articleId,
      chapterId,
      fetchComments,
      isChapterMode,
      isParagraphMode,
      paragraphId,
      postId,
      sort,
    ],
  );

  const fetchGenerationRef = useRef(0);
  const loadingRef = useRef(loading);
  const paginationRef = useRef(paginationInfo);
  const loadMoreInFlightRef = useRef(false);
  loadingRef.current = loading;
  paginationRef.current = paginationInfo;

  useEffect(() => {
    const generation = ++fetchGenerationRef.current;

    void (async () => {
      const firstPage = await fetchPage(1);
      if (generation !== fetchGenerationRef.current || !firstPage) {
        return;
      }

      setPaginationInfo({
        page: 1,
        hasNextPage: firstPage.hasNextPage,
        totalDocs: firstPage.totalDocs,
      });

      if (!firstPage.hasNextPage) {
        return;
      }

      const secondPage = await fetchPage(2);
      if (generation !== fetchGenerationRef.current || !secondPage) {
        return;
      }

      setPaginationInfo({
        page: 2,
        hasNextPage: secondPage.hasNextPage,
        totalDocs: secondPage.totalDocs,
      });
    })();
  }, [fetchPage]);

  useEffect(() => {
    onHasNextPageChange?.(paginationInfo.hasNextPage);
  }, [onHasNextPageChange, paginationInfo.hasNextPage]);

  const loadMore = useCallback(async () => {
    if (loadMoreInFlightRef.current || loadingRef.current || !paginationRef.current.hasNextPage) {
      return;
    }

    loadMoreInFlightRef.current = true;
    const nextPage = paginationRef.current.page + 1;

    try {
      const result = await fetchPage(nextPage);
      if (result) {
        setPaginationInfo({
          page: nextPage,
          hasNextPage: result.hasNextPage,
          totalDocs: result.totalDocs,
        });
      }
    } finally {
      loadMoreInFlightRef.current = false;
    }
  }, [fetchPage]);

  const handleReply = useCallback((commentId: number) => {
    setReplyingTo(commentId);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  const handleLoadReplies = useCallback(
    async (commentId: number) => {
      if (expandedReplies.has(commentId)) {
        setExpandedReplies((prev) => {
          const newSet = new Set(prev);
          newSet.delete(commentId);
          return newSet;
        });
        return;
      }

      setRepliesLoading((prev) => new Set(prev).add(commentId));
      try {
        const replies = await fetchReplies(commentId);
        setLoadedReplies((prev) => new Map(prev).set(commentId, replies));
        setExpandedReplies((prev) => new Set(prev).add(commentId));
      } catch (error) {
        console.error('Failed to load replies:', error);
      } finally {
        setRepliesLoading((prev) => {
          const newSet = new Set(prev);
          newSet.delete(commentId);
          return newSet;
        });
      }
    },
    [expandedReplies, fetchReplies],
  );

  const handleReplySubmit = useCallback(
    (parentCommentId: number, newComment: Comment) => {
      setLoadedReplies((prev) => {
        const newMap = new Map(prev);
        const existingReplies = newMap.get(parentCommentId) || [];
        newMap.set(parentCommentId, [newComment, ...existingReplies]);
        return newMap;
      });

      setReplyingTo(null);

      if (!expandedReplies.has(parentCommentId)) {
        setExpandedReplies((prev) => new Set(prev).add(parentCommentId));
      }

      setPaginationInfo((prev) => ({ ...prev, totalDocs: prev.totalDocs + 1 }));
      onCommentsChange?.();
    },
    [expandedReplies, onCommentsChange],
  );

  const retryFetch = useCallback(() => {
    void fetchPage(1).then((result) => {
      if (result) {
        setPaginationInfo({
          page: 1,
          hasNextPage: result.hasNextPage,
          totalDocs: result.totalDocs,
        });
      }
    });
  }, [fetchPage]);

  const handleDeleteComment = useCallback(
    async (commentId: number) => {
      const result = await deleteComment(commentId);
      if (result.ok) {
        setLoadedReplies((prev) => {
          const next = new Map(prev);
          for (const [parentId, replies] of next) {
            next.set(
              parentId,
              replies.filter((reply) => reply.id !== commentId),
            );
          }
          next.delete(commentId);
          return next;
        });
        setPaginationInfo((prev) => ({
          ...prev,
          totalDocs: Math.max(0, prev.totalDocs - 1),
        }));
        onCommentsChange?.();
      }
      return result;
    },
    [deleteComment, onCommentsChange],
  );

  const handleUpdateComment = useCallback(
    async (commentId: number, content: string) => {
      const result = await updateComment(commentId, content);
      if (result.ok) {
        const editedAt = new Date().toISOString();
        setLoadedReplies((prev) => {
          const next = new Map(prev);
          for (const [parentId, replies] of next) {
            next.set(
              parentId,
              replies.map((reply) =>
                reply.id === commentId
                  ? { ...reply, content, isEdited: true, editedAt, updatedAt: editedAt }
                  : reply,
              ),
            );
          }
          return next;
        });
        onCommentsChange?.();
      }
      return result;
    },
    [onCommentsChange, updateComment],
  );

  return {
    comments,
    loading,
    error,
    topLevelComments,
    isParagraphMode,
    isChapterMode,
    replyingTo,
    expandedReplies,
    repliesLoading,
    loadedReplies,
    handleReply,
    handleCancelReply,
    handleLoadReplies,
    handleReplySubmit,
    handleDeleteComment,
    handleUpdateComment,
    retryFetch,
    loadMore,
    hasNextPage: paginationInfo.hasNextPage,
    totalDocs: paginationInfo.totalDocs,
    reportComment,
    ...reactions,
  };
}
